import useScrollPosition from "@react-hook/window-scroll";
import { DownOutlined } from "@ant-design/icons";
import { Dropdown, Grid, Layout, Menu, Row } from "antd";
import React, { useEffect } from "react";
import intl from "react-intl-universal";
import { shallowEqual, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Flex, Link, Spacer, Text } from "src/components";
import { requestLogin } from "src/redux/modules/Login";
import { cleanSignup } from "src/redux/modules/Signup/actions";
import { cleanUserInfo } from "src/redux/modules/UserInfo/actions";
import useSelector from "src/redux/store/useSelector";
import { removeToken } from "src/services/client";
import { FullManorleadLogo } from "src/static";
import { theme } from "src/theme/theme";
import {
    authTitle,
    AUTH_TITLE,
    DEFAULT_PROFILE_AVATAR,
    MARKETING_PAGES,
    MARKETING_PAGE_ROUTES,
    mediaQueries,
} from "src/utils";
import styled, { css } from "styled-components";
import { LocaleSelector } from "../Shared/localeSelector";
import { NavbarProps } from "./index";
import NavbarItem, { MarketKind, Route } from "./NavbarItem";
import UserAvatar, { resolveUserProfile } from "./UserAvatar";
import useGoogleAnalytics, { Category } from "src/utils/GoogleAnalytics";
import { ListingMenuButtonFull } from "../Shared/ListingMenuButton";

export const MenuIcon = ({ Icon }) => (
    <Icon style={{ position: "relative", top: "4px", marginLeft: "4px", height: "17px" }} />
);

export type HeaderContentItem = {
    title: string | string[];
    route?: Route | Route[];
    market?: MarketKind;
    icon: JSX.Element;
};

export const HEADER_CONTENT: HeaderContentItem[] = [
    {
        title: "navbar.buy",
        market: "Sale",
        icon: <></>,
    },
    {
        title: "navbar.rent",
        market: "Lease",
        icon: <></>,
    },
    {
        title: "navbar.development",
        route: { pathname: MARKETING_PAGE_ROUTES[MARKETING_PAGES.DEVELOPMENT], search: "" },
        icon: <></>,
    },
    {
        title: ["navbar.ai", "navbar.chatv2", "navbar.manorAgent"],
        route: [
            { pathname: MARKETING_PAGE_ROUTES[MARKETING_PAGES.CHATV2], search: "" },
            { pathname: MARKETING_PAGE_ROUTES[MARKETING_PAGES.MANORAGENT], search: "" },
        ],
        icon: <></>,
    },
    {
        title: [
            "navbar.learnMore",
            "navbar.proposedCondo",
            "navbar.agent",
            "navbar.lawyer",
            "navbar.guides",
        ],
        route: [
            { pathname: MARKETING_PAGE_ROUTES[MARKETING_PAGES.PROPOSED], search: "" },
            { pathname: MARKETING_PAGE_ROUTES[MARKETING_PAGES.AGENT], search: "" },
            { pathname: MARKETING_PAGE_ROUTES[MARKETING_PAGES.LAWYER], search: "" },
            { pathname: MARKETING_PAGE_ROUTES[MARKETING_PAGES.COMMUNITY], search: "" },
        ],
        icon: <></>,
    },
];

export const HEADER_CONTENT_LOGGED_IN = HEADER_CONTENT;

export type MenuProps = NavbarProps & {
    logged: boolean;
    dispatchAuth: (title: string, popup: boolean) => void;
};
const { useBreakpoint } = Grid;
const { Content } = Layout;

const DesktopMenu: React.FC<MenuProps> = ({
    children,
    $isImageAsBackground = false,
    logged,
    dispatchAuth,
}) => {
    const { pathname, search } = useLocation();
    const history = useHistory();
    const scrolly = useScrollPosition(60);
    const bp = useBreakpoint();
    const dispatch = useDispatch();
    const [avatar, setAvatar] = React.useState(DEFAULT_PROFILE_AVATAR);
    const [isAccountMenuOpen, setAccountMenuOpen] = React.useState(false);
    const loginResponse = useSelector((state) => state.login?.data ?? [], shallowEqual);
    const userInfoResponse = useSelector((state) => state.userInfo?.data ?? false, shallowEqual);
    const { sendEvent } = useGoogleAnalytics();
    const isNavTextBlack = scrolly >= 60 || window.location.pathname !== "/community";

    const handleLogout = async () => {
        sendEvent(
            Category.BUTTON,
            `User clicked on log out button: ${loginResponse.userProfile?.profileId}`,
            "DesktopMenu",
        );

        await dispatch(
            requestLogin({
                endpoint: "signout",
                body: "body",
                method: "POST",
            }),
        );

        localStorage.removeItem("refresh_token");
        removeToken();
        setAvatar(DEFAULT_PROFILE_AVATAR);
        dispatch(cleanUserInfo());
        dispatch(cleanSignup());
        dispatchAuth(authTitle(AUTH_TITLE.LOGIN), false);
    };

    const profile = resolveUserProfile(userInfoResponse, loginResponse);
    const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(" ");
    const email = profile.email;
    const isDashboard = pathname === "/profile";
    const isAccountView = isDashboard && new URLSearchParams(search).get("view") === "account";

    const menu = (
        <AccountMenu selectable={false}>
            {(fullName || email) && (
                <Menu.ItemGroup
                    key="identity"
                    title={
                        <AccountIdentity>
                            {fullName && <IdentityName>{fullName}</IdentityName>}
                            {email && <IdentityEmail>{email}</IdentityEmail>}
                        </AccountIdentity>
                    }
                />
            )}
            {(fullName || email) && <Menu.Divider />}
            <Menu.Item
                key="dashboard"
                className={isDashboard && !isAccountView ? "ant-dropdown-menu-item-selected" : undefined}
                onClick={() => history.push("/profile")}
            >
                {intl.get("navbar.dashboard")}
            </Menu.Item>
            <Menu.Item
                key="settings"
                className={isAccountView ? "ant-dropdown-menu-item-selected" : undefined}
                onClick={() => history.push("/profile?view=account")}
            >
                {intl.get("navbar.accountSetting")}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item key="logout" onClick={handleLogout}>
                {intl.get("navbar.logout")}
            </Menu.Item>
        </AccountMenu>
    );

    const headerContent = logged ? HEADER_CONTENT_LOGGED_IN : HEADER_CONTENT;

    useEffect(() => {
        if (loginResponse.cognitoJWT) {
            if (userInfoResponse.userProfile) {
                setAvatar((avatar) =>
                    userInfoResponse?.userProfile?.profilePictureUrl
                        ? userInfoResponse?.userProfile?.profilePictureUrl + "?" + Date().toString()
                        : avatar,
                );
            } else {
                setAvatar((avatar) =>
                    userInfoResponse?.profilePictureUrl
                        ? userInfoResponse?.profilePictureUrl + "?" + Date().toString()
                        : avatar,
                );
            }
        }
    }, [userInfoResponse, loginResponse]);

    return (
        <>
            <ContentWrapper
                justify="space-between"
                align="middle"
                $isImageAsBackground={$isImageAsBackground}
                scrolly={scrolly}
                style={{
                    paddingLeft: !bp.lg ? 30 : 30,
                    paddingRight: !bp.lg ? 30 : 20,
                    background: scrolly <= 60 && $isImageAsBackground ? "transparent" : "white",
                    position: $isImageAsBackground ? "fixed" : "relative",
                }}
            >
                <Flex align="center">
                    <StyledLink href="/">
                        <img src={FullManorleadLogo} alt="manorlead-logo" />
                    </StyledLink>

                    <ResponsiveSpacer initial={28} />

                    {headerContent.map(({ title, route, market, icon }) => {
                        return (
                            <NavbarItem
                                route={route}
                                market={market}
                                text={title}
                                scrolly={scrolly}
                                key={typeof title === "string" ? title : title[0]}
                                icon={icon}
                            />
                        );
                    })}
                    {window.innerWidth >= 1585 ? (
                        <React.Fragment>
                            <PhoneNumber>{"1 (647) 613-8568"}</PhoneNumber>
                        </React.Fragment>
                    ) : null}
                </Flex>

                <Flex>
                    {logged ? (
                        <TopRightGroup>
                            <Dropdown
                                overlay={menu}
                                trigger={["click"]}
                                placement="bottomRight"
                                align={{ offset: [0, 8] }}
                                visible={isAccountMenuOpen}
                                onVisibleChange={setAccountMenuOpen}
                                getPopupContainer={(trigger) =>
                                    trigger.parentElement ? trigger.parentElement : trigger
                                }
                            >
                                <AccountButton
                                    type="button"
                                    aria-label={intl.get("navbar.dashboard")}
                                    aria-haspopup="menu"
                                    aria-expanded={isAccountMenuOpen}
                                    $open={isAccountMenuOpen}
                                    $isTextBlack={isNavTextBlack}
                                >
                                    <UserAvatar
                                        photoUrl={avatar}
                                        firstName={profile.firstName}
                                        lastName={profile.lastName}
                                        email={email}
                                        size="default"
                                    />
                                    <AccountLabel
                                        $active={isDashboard}
                                        $isTextBlack={isNavTextBlack}
                                    >
                                        {intl.get("navbar.dashboard")}
                                    </AccountLabel>
                                    <AccountChevron
                                        $open={isAccountMenuOpen}
                                        $isTextBlack={isNavTextBlack}
                                    />
                                </AccountButton>
                            </Dropdown>
                            <LocaleSelector
                                ismobile={false}
                                isSelectorColorBlack={isNavTextBlack}
                            />
                        </TopRightGroup>
                    ) : (
                        <Flex style={{ alignItems: "center" }}>
                            <ResponsiveSpacer initial={8} medium={8} />
                            <StyledAuthButtons
                                onClick={() => {
                                    sendEvent(
                                        Category.BUTTON,
                                        "clicks on log in on nav bar by user",
                                        "DesktopMenu",
                                    );
                                    dispatchAuth(authTitle(AUTH_TITLE.LOGIN), true);
                                }}
                                $fontSize={window.innerWidth >= 1300 ? "16" : "14"}
                                $boxWidth={window.innerWidth >= 1300 ? "86" : "78"}
                            >
                                {intl.get("navbar.login")}
                            </StyledAuthButtons>
                            <RegisterButton
                                onClick={() => {
                                    sendEvent(
                                        Category.BUTTON,
                                        "clicks on sign up on nav bar by user",
                                        "DesktopMenu",
                                    );
                                    dispatchAuth(authTitle(AUTH_TITLE.SIGNUP), true);
                                }}
                                $fontSize={window.innerWidth >= 1400 ? "16" : "14"}
                                $boxWidth={window.innerWidth >= 1400 ? "90" : "82"}
                            >
                                {intl.get("navbar.register")}
                            </RegisterButton>
                            <LocaleSelector
                                ismobile={false}
                                isSelectorColorBlack={
                                    scrolly >= 60 || window.location.pathname !== "/community"
                                }
                            />
                        </Flex>
                    )}
                </Flex>
            </ContentWrapper>
            <Content style={{ position: "relative" }}>{children}</Content>
        </>
    );
};

export default DesktopMenu;

const TopRightGroup = styled.div`
    align-items: center;
    display: flex;
    gap: 2px;
`;

const AccountChevron = styled(DownOutlined)<{
    $open: boolean;
    $isTextBlack: boolean;
}>`
    color: ${({ $isTextBlack, theme }) =>
        $isTextBlack ? theme.color.black : theme.color.white};
    transition: transform 0.2s ease;
    transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});

    svg {
        display: block;
        height: 14px;
        width: 14px;
    }
`;

const AccountLabel = styled.span<{
    $active: boolean;
    $isTextBlack: boolean;
}>`
    font-family: ${({ theme }) => theme.fontFamily.homeBody};
    font-size: ${({ theme }) => theme.fontSize.bodyM}px;
    font-weight: ${({ $active }) => ($active ? 700 : 400)};
    letter-spacing: -0.01em;
    line-height: 1;
    color: ${({ $isTextBlack, theme }) =>
        $isTextBlack ? theme.color.black : theme.color.white};
    white-space: nowrap;
`;

const AccountButton = styled.button<{
    $open: boolean;
    $isTextBlack: boolean;
}>`
    appearance: none;
    border: none;
    margin: 0;
    padding: 3px 10px 3px 3px;
    border-radius: 980px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 0;
    transition: background-color 0.15s ease;
    background-color: ${({ $open, $isTextBlack }) =>
        !$open ? "transparent" : $isTextBlack ? "#f5f5f7" : "rgba(255, 255, 255, 0.2)"};

    &:hover {
        background-color: ${({ $isTextBlack }) =>
            $isTextBlack ? "#f5f5f7" : "rgba(255, 255, 255, 0.2)"};
    }

    &:focus-visible {
        outline: 2px solid
            ${({ $isTextBlack }) => ($isTextBlack ? "#1d1d1f" : theme.color.white)};
        outline-offset: 2px;
    }
`;

const AccountIdentity = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-width: 240px;
`;

const IdentityName = styled.div`
    font-family: ${({ theme }) => theme.fontFamily.body};
    font-size: ${({ theme }) => theme.fontSize.bodyM}px;
    font-weight: 500;
    letter-spacing: -0.01em;
    line-height: 1.3;
    color: #1d1d1f;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const IdentityEmail = styled.div`
    font-family: ${({ theme }) => theme.fontFamily.body};
    font-size: ${({ theme }) => theme.fontSize.bodyS}px;
    font-weight: 400;
    line-height: 1.3;
    color: #86868b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const AccountMenu = styled(Menu)`
    min-width: 196px;
    border-radius: 8px;

    .ant-dropdown-menu-item-group-title {
        padding: 12px 16px;
        cursor: default;
    }

    .ant-dropdown-menu-item-group-list {
        margin: 0;
    }

    .ant-dropdown-menu-item {
        margin: 0;
        padding: 8px 16px;
        font-family: ${({ theme }) => theme.fontFamily.body};
        font-size: ${({ theme }) => theme.fontSize.bodyM}px;
        font-weight: 400;
        letter-spacing: -0.01em;
        line-height: 1.4;
        color: #1d1d1f;
    }

    .ant-dropdown-menu-item:hover,
    .ant-dropdown-menu-item-active,
    .ant-dropdown-menu-item-selected {
        background: #f5f5f7;
        color: #1d1d1f;
    }

    .ant-dropdown-menu-item-divider {
        margin: 0;
        background: rgba(0, 0, 0, 0.06);
    }
`;

const ContentWrapper = styled(Row)<{
    $isImageAsBackground?: boolean;
    scrolly: any;
}>`
    width: 100%;
    z-index: 10;
    height: 64px;
    min-height: 64px;
    box-shadow: ${({ scrolly, $isImageAsBackground }) =>
        $isImageAsBackground && scrolly <= 60 ? "none" : "rgba(0, 0, 0, 0.08) 0px 1px 12px"};
`;

const linkTextHighlightedStyles = css`
    font-weight: 700;
`;

export const LinkText = styled.p<{
    $isRouteSelected?: boolean;
    scrolly?: any;
}>`
    font-family: ${({ theme }) => theme.fontFamily.homeBody};
    font-size: ${({ theme }) => theme.fontSize.bodyM}px;
    ${({ theme }) => theme.styleGroup.body}
    color: ${({ scrolly }) =>
        scrolly <= 60 && window.location.pathname === "/community"
            ? theme.color.white
            : theme.color.black};

    ${({ $isRouteSelected }) => $isRouteSelected && linkTextHighlightedStyles}

    ${mediaQueries.medium} {
        font-size: ${({ theme }) => theme.fontSize.bodyM}px;
    }
`;

const PhoneNumber = styled(Text)`
    font-family: ${({ theme }) => theme.fontFamily.homeBody};
    font-size: ${({ theme }) => theme.fontSize.bodyM}px;
    ${({ theme }) => theme.styleGroup.body}
    color: ${({ theme }) => theme.color.goldAccent};
    font-weight: 700;
`;

const StyledLink = styled(Link)`
    display: flex;
    align-items: center;
`;

const ResponsiveSpacer = styled(Spacer)<{
    initial: number;
    large?: number;
    medium?: number;
}>`
    width: ${({ initial }) => initial}px;

    ${({ large }) =>
        large &&
        `${mediaQueries.large} {
        width: ${large}px;
    }`}

    ${({ medium }) =>
        medium &&
        `${mediaQueries.medium} {
        width: ${medium}px;
    }`}
`;

const StyledAuthButtons = styled(ListingMenuButtonFull)<{
    $fontSize: string;
    $boxWidth: string;
}>`
    width: ${({ $boxWidth }) => `${$boxWidth}px`};
    height: 35px;
    font-size: ${({ $fontSize }) => `${$fontSize}px`};
    font-weight: 400;
`;

const RegisterButton = styled(StyledAuthButtons)`
    color: ${({ theme }) => theme.color.black};
    background-color: ${({ theme }) => theme.color.manorleadGold};

    :hover {
        color: ${({ theme }) => theme.color.black};
        background-color: ${({ theme }) => theme.color.manorleadGold};
    }
`;
